import { useEffect, useRef, useState } from "react";
import cytoscape from "cytoscape";
import {
  NETWORKING_BANDS,
  NETWORKING_CHIP_BACKGROUND,
  NETWORKING_CHIP_TEXT,
  NETWORKING_RING_COLOR,
  getBandForPercent,
  type NetworkingBand,
} from "./networkingMapBands";
import { getRootId } from "./networkingMapCenter";
import {
  BAND_LABEL_WIDTH,
  PERSON_NODE_SIZE_NORMAL,
  PERSON_NODE_SIZE_ROOT,
  computeHoverNodeSize,
  computeOrbitAngles,
  computeOrbitNodeSize,
} from "./networkingMapNodeSizing";
import type { NetworkingMapNode, NetworkingMapResponse } from "./networkingMapTypes";
import NetworkingMapBandLabelTooltip from "./NetworkingMapBandLabelTooltip";
import NetworkingMapPersonMiniPanel from "./NetworkingMapPersonMiniPanel";
import NetworkingMapZoomControls from "./NetworkingMapZoomControls";
import "./NetworkingMapGraph.css";

const RING_NODE_PREFIX = "ring-";
const BAND_LABEL_PREFIX = "band-label-";
const BAND_LABEL_ICON_PREFIX = "band-label-icon-";
const RING_HIGHLIGHT_COLOR = "#9C3325";
const RING_HIGHLIGHT_WIDTH = 2;
const MINI_PANEL_WIDTH = 280;
const MINI_PANEL_OFFSET = 16;
const MINI_PANEL_HIDE_DELAY_MS = 100;
const BAND_LABEL_FONT_SIZE = 14;
const BAND_LABEL_ICON_SIZE = 17;
const BAND_LABEL_ICON_INSET = 8;
const FIT_PADDING = 30;
const ZOOM_STEP = 1.25;
const MAX_NODE_VIEWPORT_FRACTION = 0.42;
const ZOOM_EPSILON = 0.001;

type MiniPanelState = {
  node: NetworkingMapNode["data"];
  x: number;
  y: number;
};

type BandLabelTooltipState = {
  text: string;
  x: number;
  y: number;
};

function getMiniPanelPosition(
  node: cytoscape.NodeSingular,
  containerWidth: number,
): Pick<MiniPanelState, "x" | "y"> {
  const pos = node.renderedPosition();
  const halfW = node.renderedOuterWidth() / 2;
  const halfH = node.renderedOuterHeight() / 2;
  let x = pos.x + halfW + MINI_PANEL_OFFSET;
  let y = pos.y - halfH;

  if (x + MINI_PANEL_WIDTH > containerWidth - 8) {
    x = pos.x - halfW - MINI_PANEL_WIDTH - MINI_PANEL_OFFSET;
  }

  return { x: Math.max(8, x), y: Math.max(8, y) };
}

function getBandLabelTooltipPosition(
  chip: cytoscape.NodeSingular,
): Pick<BandLabelTooltipState, "x" | "y"> {
  const box = chip.renderedBoundingBox();

  return {
    x: box.x2,
    y: box.y2 + 6,
  };
}

function getBandLabelChip(
  cy: cytoscape.Core,
  bandKey: string,
): cytoscape.NodeSingular {
  return cy.getElementById(`${BAND_LABEL_PREFIX}${bandKey}`);
}

type NetworkingMapGraphProps = {
  data: NetworkingMapResponse;
  className?: string;
  selectedNodeId?: string | null;
  onSelectNode?: (nodeId: string | null) => void;
};

type ZoomUiState = {
  zoom: number;
  min: number;
  max: number;
};

function applyViewportZoomLimits(cy: cytoscape.Core) {
  const container = cy.container();
  if (!container) {
    return;
  }

  const width = container.clientWidth;
  const height = container.clientHeight;
  if (width < 8 || height < 8) {
    return;
  }

  const extent = cy.elements().boundingBox();
  const fitZoom = Math.min(
    width / Math.max(1, extent.w + FIT_PADDING * 2),
    height / Math.max(1, extent.h + FIT_PADDING * 2),
  );
  const maxByAvatar =
    (Math.min(width, height) * MAX_NODE_VIEWPORT_FRACTION) /
    PERSON_NODE_SIZE_NORMAL;
  const minZoom = Math.max(0.05, fitZoom);
  const maxZoom = Math.max(minZoom + 0.05, maxByAvatar);

  cy.minZoom(minZoom);
  cy.maxZoom(maxZoom);

  const current = cy.zoom();
  if (current < minZoom) {
    cy.zoom(minZoom);
  }
  if (current > maxZoom) {
    cy.zoom(maxZoom);
  }
}

function fitGraphToViewport(cy: cytoscape.Core) {
  cy.fit(cy.elements(), FIT_PADDING);
  applyViewportZoomLimits(cy);
  cy.fit(cy.elements(), FIT_PADDING);
}

function zoomGraphByFactor(cy: cytoscape.Core, factor: number) {
  const container = cy.container();
  const next = cy.zoom() * factor;
  if (container) {
    cy.zoom({
      level: next,
      renderedPosition: {
        x: container.clientWidth / 2,
        y: container.clientHeight / 2,
      },
    });
    return;
  }
  cy.zoom(next);
}

function isClickablePerson(node: cytoscape.NodeSingular) {
  return node.data("kind") === "person" && node.data("isRoot") !== "true";
}

/** Rings and their labels are inert nodes so they pan and zoom with the graph. */
function buildDecorationElements(): cytoscape.ElementDefinition[] {
  const decorations: cytoscape.ElementDefinition[] = [];

  NETWORKING_BANDS.forEach((band) => {
    const tagY = -band.radius;
    const iconX =
      BAND_LABEL_WIDTH / 2 - BAND_LABEL_ICON_INSET - BAND_LABEL_ICON_SIZE / 2;

    decorations.push({
      group: "nodes",
      data: { id: `${RING_NODE_PREFIX}${band.key}`, kind: "ring", band: band.key },
      position: { x: 0, y: 0 },
      selectable: false,
      grabbable: false,
      pannable: true,
      locked: true,
    });

    decorations.push({
      group: "nodes",
      data: {
        id: `${BAND_LABEL_PREFIX}${band.key}`,
        kind: "bandLabel",
        band: band.key,
        label: band.label,
        description: band.description,
      },
      position: { x: 0, y: tagY },
      selectable: false,
      grabbable: false,
      pannable: true,
      locked: true,
    });

    decorations.push({
      group: "nodes",
      data: {
        id: `${BAND_LABEL_ICON_PREFIX}${band.key}`,
        kind: "bandLabelIcon",
        band: band.key,
        label: "ⓘ",
        description: band.description,
      },
      position: { x: iconX, y: tagY },
      selectable: false,
      grabbable: false,
      pannable: true,
      locked: true,
    });
  });

  return decorations;
}

function buildStyles(): cytoscape.StylesheetStyle[] {
  const bandStyles = NETWORKING_BANDS.flatMap((band: NetworkingBand) => [
    {
      selector: `node[kind = "ring"][band = "${band.key}"]`,
      style: {
        width: band.radius * 2,
        height: band.radius * 2,
        "border-width": band.ringWidth,
        "border-color": band.ringColor,
      },
    },
    {
      selector: `node[kind = "ring"][band = "${band.key}"].networking-ring-highlighted`,
      style: {
        "border-color": RING_HIGHLIGHT_COLOR,
        "border-width": RING_HIGHLIGHT_WIDTH,
      },
    },
    {
      selector: `node[kind = "bandLabel"][band = "${band.key}"]`,
      style: { "background-color": band.chipColor },
    },
  ]);

  return [
    {
      selector: 'node[kind = "ring"]',
      style: {
        shape: "ellipse",
        "background-opacity": 0,
        "z-index": 1,
        "transition-property": "border-color border-width",
        "transition-duration": 150,
      },
    },
    {
      selector: 'node[kind = "bandLabel"]',
      style: {
        shape: "round-rectangle",
        label: "data(label)",
        color: NETWORKING_CHIP_TEXT,
        "background-color": NETWORKING_CHIP_BACKGROUND,
        "border-width": 1,
        "border-color": NETWORKING_CHIP_BACKGROUND,
        "corner-radius": 8,
        "font-family": "Roboto",
        "font-size": BAND_LABEL_FONT_SIZE,
        "font-weight": 500,
        "text-valign": "center",
        "text-halign": "center",
        "text-margin-x": -10,
        width: BAND_LABEL_WIDTH,
        height: 28,
        "z-index": 15,
        events: "no",
      },
    },
    {
      selector: 'node[kind = "bandLabelIcon"]',
      style: {
        shape: "ellipse",
        width: BAND_LABEL_ICON_SIZE,
        height: BAND_LABEL_ICON_SIZE,
        "background-opacity": 0,
        label: "data(label)",
        color: NETWORKING_CHIP_TEXT,
        "font-family": "Roboto",
        "font-size": 14,
        "font-weight": 500,
        "text-valign": "center",
        "text-halign": "center",
        "z-index": 16,
      },
    },
    ...bandStyles,
    {
      selector: 'node[kind = "person"]',
      style: {
        shape: "ellipse",
        width: "data(size)",
        height: "data(size)",
        "background-color": "#94a3b8",
        "background-fit": "cover",
        "background-clip": "node",
        "background-image": (element: cytoscape.NodeSingular) =>
          element.data("avatar") || "none",
        "background-image-crossorigin": "null" as "anonymous",
        "border-width": 0,
        "z-index": 10,
        "shadow-opacity": 0,
        "transition-property": "width height border-width shadow-opacity shadow-blur",
        "transition-duration": 150,
      },
    },
    {
      selector: 'node[kind = "person"][isRoot = "true"]',
      style: {
        width: PERSON_NODE_SIZE_ROOT,
        height: PERSON_NODE_SIZE_ROOT,
        "border-width": 4,
        "border-color": "#123F63",
        "z-index": 20,
      },
    },
    {
      selector: 'node[kind = "person"]:selected',
      style: {
        width: "data(normalSize)",
        height: "data(normalSize)",
        "border-width": 4,
        "border-color": "#9C3325",
        "overlay-color": "#9C3325",
        "overlay-opacity": 0.45,
        "overlay-padding": 14,
        "overlay-shape": "ellipse",
        "z-index": 30,
      },
    },
    {
      selector: 'node[kind = "person"].networking-node-hovered',
      style: {
        width: "data(hoverSize)",
        height: "data(hoverSize)",
        "border-width": 4,
        "border-color": "#9C3325",
        "shadow-blur": 10,
        "shadow-color": "#000000",
        "shadow-opacity": 0.28,
        "shadow-offset-x": 0,
        "shadow-offset-y": 3,
        "z-index": 25,
      },
    },
  ] as cytoscape.StylesheetStyle[];
}

function applyNodeDisplaySize(node: cytoscape.NodeSingular) {
  const isSelected = node.selected();
  const isHovered = node.hasClass("networking-node-hovered");
  const compactSize = Number(node.data("compactSize"));
  const normalSize = Number(node.data("normalSize"));
  const hoverSize = Number(node.data("hoverSize"));

  node.data(
    "size",
    isHovered ? hoverSize : isSelected ? normalSize : compactSize,
  );
}

function getBandKeyFromModelPosition(x: number, y: number): string {
  const distance = Math.hypot(x, y);
  let closest = NETWORKING_BANDS[0];
  let closestDiff = Infinity;

  for (const band of NETWORKING_BANDS) {
    const diff = Math.abs(distance - band.radius);
    if (diff < closestDiff) {
      closestDiff = diff;
      closest = band;
    }
  }

  return closest.key;
}

function syncRingHighlights(
  cy: cytoscape.Core,
  ringHoverBand: string | null,
  personHoverBand: string | null,
  selectedBand: string | null,
) {
  const activeBand = ringHoverBand ?? personHoverBand ?? selectedBand;

  cy.nodes('[kind = "ring"]').forEach((node) => {
    const bandKey = String(node.data("band") ?? "");
    const band = NETWORKING_BANDS.find((entry) => entry.key === bandKey);
    const isHighlighted = Boolean(activeBand && bandKey === activeBand);

    node.toggleClass("networking-ring-highlighted", isHighlighted);
    node.style({
      "border-color": isHighlighted
        ? RING_HIGHLIGHT_COLOR
        : (band?.ringColor ?? NETWORKING_RING_COLOR),
      "border-width": isHighlighted
        ? RING_HIGHLIGHT_WIDTH
        : (band?.ringWidth ?? 1),
    });
  });
}

function countAttendeesPerBand(
  data: NetworkingMapResponse,
  rootId?: string,
): Map<string, number> {
  const counts = new Map<string, number>();

  data.nodes.forEach((node) => {
    if (node.data.id === rootId) {
      return;
    }
    const band = getBandForPercent(node.data.affinityPercent ?? 0);
    counts.set(band.key, (counts.get(band.key) ?? 0) + 1);
  });

  return counts;
}

function applyOrbitLayout(cy: cytoscape.Core, rootId: string) {
  const root = cy.nodes().toArray().find((node) => node.id() === rootId);
  root?.position({ x: 0, y: 0 });

  NETWORKING_BANDS.forEach((band) => {
    const nodesInBand = cy
      .nodes(`[kind = "person"][band = "${band.key}"]`)
      .toArray()
      .filter((node) => node.id() !== rootId);

    if (nodesInBand.length === 0) {
      return;
    }

    const maxNodeSize = Math.max(
      ...nodesInBand.map((node) => Number(node.data("normalSize"))),
    );
    const angles = computeOrbitAngles(
      band.radius,
      nodesInBand.length,
      maxNodeSize,
    );

    nodesInBand.forEach((node, index) => {
      const angle = angles[index];
      node.position({
        x: band.radius * Math.cos(angle),
        y: band.radius * Math.sin(angle),
      });
    });
  });
}

export default function NetworkingMapGraph({
  data,
  className = "",
  selectedNodeId = null,
  onSelectNode,
}: NetworkingMapGraphProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const cyRef = useRef<cytoscape.Core | null>(null);
  const hoveredNodeRef = useRef<cytoscape.NodeSingular | null>(null);
  const previewNodeIdRef = useRef<string | null>(null);
  const miniPanelHoveredRef = useRef(false);
  const hideMiniPanelTimeoutRef = useRef<number | null>(null);
  const hoveredBandLabelRef = useRef<cytoscape.NodeSingular | null>(null);
  const ringHoverBandRef = useRef<string | null>(null);
  const personHoverBandRef = useRef<string | null>(null);
  const selectedBandRef = useRef<string | null>(null);
  const onSelectNodeRef = useRef(onSelectNode);
  const [miniPanel, setMiniPanel] = useState<MiniPanelState | null>(null);
  const [bandLabelTooltip, setBandLabelTooltip] =
    useState<BandLabelTooltipState | null>(null);
  const [zoomUi, setZoomUi] = useState<ZoomUiState>({
    zoom: 1,
    min: 0.3,
    max: 2,
  });
  onSelectNodeRef.current = onSelectNode;

  const syncZoomUi = (cy: cytoscape.Core) => {
    setZoomUi({
      zoom: cy.zoom(),
      min: cy.minZoom(),
      max: cy.maxZoom(),
    });
  };

  const syncRingHighlightsFromRefs = (cy: cytoscape.Core) => {
    syncRingHighlights(
      cy,
      ringHoverBandRef.current,
      personHoverBandRef.current,
      selectedBandRef.current,
    );
  };

  const clearHideMiniPanelTimeout = () => {
    if (hideMiniPanelTimeoutRef.current != null) {
      window.clearTimeout(hideMiniPanelTimeoutRef.current);
      hideMiniPanelTimeoutRef.current = null;
    }
  };

  const clearPersonPreview = (cy: cytoscape.Core) => {
    previewNodeIdRef.current = null;

    cy.nodes('[kind = "person"].networking-node-hovered').forEach((node) => {
      if (!node.selected()) {
        node.removeClass("networking-node-hovered");
        applyNodeDisplaySize(node);
      }
    });

    personHoverBandRef.current = null;
    syncRingHighlightsFromRefs(cy);
    setMiniPanel(null);
  };

  const forceClearPersonPreview = (cy: cytoscape.Core) => {
    clearHideMiniPanelTimeout();
    hoveredNodeRef.current = null;
    miniPanelHoveredRef.current = false;
    clearPersonPreview(cy);
  };

  const scheduleHidePersonPreview = (cy: cytoscape.Core) => {
    clearHideMiniPanelTimeout();
    hideMiniPanelTimeoutRef.current = window.setTimeout(() => {
      hideMiniPanelTimeoutRef.current = null;
      if (hoveredNodeRef.current || miniPanelHoveredRef.current) {
        return;
      }
      clearPersonPreview(cy);
    }, MINI_PANEL_HIDE_DELAY_MS);
  };

  const showPersonPreview = (cy: cytoscape.Core, node: cytoscape.NodeSingular) => {
    clearHideMiniPanelTimeout();

    const previousId = previewNodeIdRef.current;
    if (previousId && previousId !== node.id()) {
      const previous = cy.getElementById(previousId);
      if (previous.length && !previous.selected()) {
        previous.removeClass("networking-node-hovered");
        applyNodeDisplaySize(previous);
      }
    }

    previewNodeIdRef.current = node.id();
    node.addClass("networking-node-hovered");
    applyNodeDisplaySize(node);
    personHoverBandRef.current = String(node.data("band") ?? "") || null;
    syncRingHighlightsFromRefs(cy);
    updateMiniPanel(cy, node.id());
  };

  const updateMiniPanel = (cy: cytoscape.Core, nodeId: string | null) => {
    if (!nodeId || !wrapperRef.current) {
      setMiniPanel(null);
      return;
    }

    const graphNode = data.nodes.find((entry) => entry.data.id === nodeId);
    const cyNode = cy.getElementById(nodeId);
    if (!graphNode || !cyNode.length || cyNode.data("isRoot") === "true") {
      setMiniPanel(null);
      return;
    }

    const { x, y } = getMiniPanelPosition(
      cyNode,
      wrapperRef.current.clientWidth,
    );
    setMiniPanel({ node: graphNode.data, x, y });
  };

  const updateBandLabelTooltip = (
    node: cytoscape.NodeSingular | null,
    cy: cytoscape.Core,
  ) => {
    if (!node) {
      setBandLabelTooltip(null);
      return;
    }

    const bandKey = String(node.data("band") ?? "");
    const chip = getBandLabelChip(cy, bandKey);
    if (!chip.length) {
      setBandLabelTooltip(null);
      return;
    }

    const { x, y } = getBandLabelTooltipPosition(chip);
    setBandLabelTooltip({
      text: String(node.data("description") ?? ""),
      x,
      y,
    });
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const rootId = getRootId(data);
    const attendeesPerBand = countAttendeesPerBand(data, rootId);

    const personNodes: cytoscape.ElementDefinition[] = data.nodes.map((node) => {
      const isRoot = node.data.id === rootId;
      const band = getBandForPercent(node.data.affinityPercent ?? 100);
      const bandCount = attendeesPerBand.get(band.key) ?? 0;
      const compactSize = isRoot
        ? PERSON_NODE_SIZE_ROOT
        : computeOrbitNodeSize(band.radius, bandCount);
      const normalSize = isRoot ? PERSON_NODE_SIZE_ROOT : PERSON_NODE_SIZE_NORMAL;
      const hoverSize = isRoot
        ? PERSON_NODE_SIZE_ROOT
        : computeHoverNodeSize(compactSize, normalSize);

      return {
        group: "nodes",
        data: {
          ...node.data,
          kind: "person",
          band: band.key,
          ringColor: isRoot ? "#123F63" : band.ringColor,
          isRoot: isRoot ? "true" : "false",
          compactSize,
          normalSize,
          hoverSize,
          size: compactSize,
        },
        grabbable: false,
        pannable: true,
      };
    });

    // Edges are intentionally not rendered; the rings convey affinity instead.
    const cy = cytoscape({
      container,
      elements: [...buildDecorationElements(), ...personNodes],
      style: buildStyles(),
      layout: { name: "preset" },
      userZoomingEnabled: false,
      minZoom: 0.3,
      maxZoom: 2,
    });

    if (rootId) {
      applyOrbitLayout(cy, rootId);
    }
    fitGraphToViewport(cy);
    syncZoomUi(cy);
    cyRef.current = cy;

    cy.on("background", () => {
      cy.style().update();
    });

    cy.on("tap", 'node[kind = "person"]', (event) => {
      const node = event.target;
      if (node.data("isRoot") === "true") {
        return;
      }
      onSelectNodeRef.current?.(node.id());
    });

    cy.on("tap", (event) => {
      if (event.target === cy) {
        onSelectNodeRef.current?.(null);
      }
    });

    cy.on("mouseover", 'node[kind = "ring"]', (event) => {
      const { x, y } = event.position;
      ringHoverBandRef.current = getBandKeyFromModelPosition(x, y);
      syncRingHighlightsFromRefs(cy);
    });

    cy.on("mouseout", 'node[kind = "ring"]', () => {
      ringHoverBandRef.current = null;
      syncRingHighlightsFromRefs(cy);
    });

    cy.on("mouseover", 'node[kind = "bandLabelIcon"]', (event) => {
      const node = event.target;
      hoveredBandLabelRef.current = node;
      wrapperRef.current?.classList.add("is-over-help");
      updateBandLabelTooltip(node, cy);
    });

    cy.on("mouseout", 'node[kind = "bandLabelIcon"]', () => {
      hoveredBandLabelRef.current = null;
      wrapperRef.current?.classList.remove("is-over-help");
      setBandLabelTooltip(null);
    });

    cy.on("mouseover", 'node[kind = "person"]', (event) => {
      const node = event.target;
      hoveredNodeRef.current = node;
      if (isClickablePerson(node)) {
        wrapperRef.current?.classList.add("is-over-clickable");
        showPersonPreview(cy, node);
      }
    });

    cy.on("mouseout", 'node[kind = "person"]', (event) => {
      const node = event.target;
      wrapperRef.current?.classList.remove("is-over-clickable");
      if (hoveredNodeRef.current === node) {
        hoveredNodeRef.current = null;
      }

      if (previewNodeIdRef.current === node.id()) {
        scheduleHidePersonPreview(cy);
      }
    });

    const handlePanStart = (event: cytoscape.EventObject) => {
      const original = event.originalEvent as MouseEvent | undefined;
      if (original && original.button !== 0) {
        return;
      }
      wrapperRef.current?.classList.add("is-panning");
    };

    const handlePanEnd = () => {
      wrapperRef.current?.classList.remove("is-panning");
    };

    cy.on("mousedown", handlePanStart);
    cy.on("mouseup", handlePanEnd);
    window.addEventListener("mouseup", handlePanEnd);

    cy.on("pan zoom", () => {
      if (hoveredBandLabelRef.current) {
        updateBandLabelTooltip(hoveredBandLabelRef.current, cy);
      }
      if (previewNodeIdRef.current) {
        updateMiniPanel(cy, previewNodeIdRef.current);
      }
    });

    cy.on("zoom", () => {
      syncZoomUi(cy);
    });

    const preventWheelZoom = (_event: WheelEvent) => {
      // Intentionally empty: wheel must not zoom or pan the map.
    };
    container.addEventListener("wheel", preventWheelZoom, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      cy.resize();
      fitGraphToViewport(cy);
      syncZoomUi(cy);
      if (hoveredBandLabelRef.current) {
        updateBandLabelTooltip(hoveredBandLabelRef.current, cy);
      }
      if (previewNodeIdRef.current) {
        updateMiniPanel(cy, previewNodeIdRef.current);
      }
    });
    resizeObserver.observe(container);

    const handleWrapperMouseLeave = () => {
      wrapperRef.current?.classList.remove(
        "is-panning",
        "is-over-clickable",
        "is-over-help",
      );
      forceClearPersonPreview(cy);
    };

    const wrapper = wrapperRef.current;
    wrapper?.addEventListener("mouseleave", handleWrapperMouseLeave);

    return () => {
      wrapper?.removeEventListener("mouseleave", handleWrapperMouseLeave);
      window.removeEventListener("mouseup", handlePanEnd);
      container.removeEventListener("wheel", preventWheelZoom);
      resizeObserver.disconnect();
      clearHideMiniPanelTimeout();
      hoveredNodeRef.current = null;
      previewNodeIdRef.current = null;
      miniPanelHoveredRef.current = false;
      hoveredBandLabelRef.current = null;
      ringHoverBandRef.current = null;
      personHoverBandRef.current = null;
      selectedBandRef.current = null;
      setMiniPanel(null);
      setBandLabelTooltip(null);
      cy.destroy();
      cyRef.current = null;
    };
  }, [data]);

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) {
      return;
    }

    cy.nodes('[kind = "person"]').forEach((node) => {
      node.unselect();
      node.removeClass("networking-node-hovered");
      applyNodeDisplaySize(node);
    });

    if (selectedNodeId) {
      const selected = cy.getElementById(selectedNodeId);
      selected.select();
      applyNodeDisplaySize(selected);
      selectedBandRef.current =
        selected.data("isRoot") === "true"
          ? null
          : String(selected.data("band") ?? "") || null;
    } else {
      selectedBandRef.current = null;
    }

    syncRingHighlightsFromRefs(cy);
  }, [selectedNodeId]);

  return (
    <div
      ref={wrapperRef}
      className={`networking-map-graph relative w-full ${className || "h-[560px]"}`.trim()}
    >
      <div
        ref={containerRef}
        className="h-full w-full"
        aria-label="Networking map graph"
      />
      <NetworkingMapZoomControls
        canZoomIn={zoomUi.zoom < zoomUi.max - ZOOM_EPSILON}
        canZoomOut={zoomUi.zoom > zoomUi.min + ZOOM_EPSILON}
        onZoomIn={() => {
          const cy = cyRef.current;
          if (!cy) {
            return;
          }
          zoomGraphByFactor(cy, ZOOM_STEP);
        }}
        onZoomOut={() => {
          const cy = cyRef.current;
          if (!cy) {
            return;
          }
          zoomGraphByFactor(cy, 1 / ZOOM_STEP);
        }}
        onReset={() => {
          const cy = cyRef.current;
          if (!cy) {
            return;
          }
          fitGraphToViewport(cy);
          syncZoomUi(cy);
        }}
      />
      {bandLabelTooltip && (
        <NetworkingMapBandLabelTooltip
          text={bandLabelTooltip.text}
          x={bandLabelTooltip.x}
          y={bandLabelTooltip.y}
        />
      )}
      {miniPanel && (
        <NetworkingMapPersonMiniPanel
          node={miniPanel.node}
          x={miniPanel.x}
          y={miniPanel.y}
          onOpenProfile={() => onSelectNodeRef.current?.(miniPanel.node.id)}
          onMouseEnter={() => {
            const cy = cyRef.current;
            if (!cy) {
              return;
            }
            miniPanelHoveredRef.current = true;
            clearHideMiniPanelTimeout();
          }}
          onMouseLeave={() => {
            const cy = cyRef.current;
            if (!cy) {
              return;
            }
            miniPanelHoveredRef.current = false;
            scheduleHidePersonPreview(cy);
          }}
        />
      )}
    </div>
  );
}
